// 📄 pages/Proyectos/components/CasoPruebaFormModal.tsx
// Alta/edición de un caso de prueba — la parte distinta del resto de
// formularios de la plataforma es la lista de "campos" (nombre/valor)
// libres: el usuario agrega tantos como necesite el escenario (sociedad,
// proveedor, material, etc.), no hay un catálogo fijo por canal.
import React, { useState } from 'react';
import { X, Save, RefreshCw, Plus, Trash2, Paperclip } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { CANALES_COMMERCE_ITEMS } from '../../Dashboard/menuConfig.tsx';
import { getCanalesDeSolucion } from '../integrationChannels.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { UploadedAttachment } from '../../../services/storageService.ts';
import {
  CasoPrueba, CasoPruebaFormData, CampoPrueba,
  createCasoPrueba, updateCasoPruebaDatos
} from '../../../services/casoPruebaService.ts';

interface CasoPruebaFormModalProps {
  projectId: string;
  solucionesActivas: string[];
  // Proyecto sin SAP Ariba: el campo es "Área" (opcional) en vez de "Solución relacionada".
  modoArea?: boolean;
  caso?: CasoPrueba | null; // presente = edición
  onClose: () => void;
  onSaved: () => void;
}

// Opciones de canal para una solución dada: Commerce usa su propio listado
// de sidebar (CANALES_COMMERCE_ITEMS), las demás soluciones con checklist
// de integración usan integrationChannels.ts — algunas soluciones (Buying,
// CIG) todavía no tienen un catálogo de canales, quedan sin selector.
export const canalesDe = (solucion: string): { id: string; label: string }[] => {
  if (solucion === 'commerce') {
    return CANALES_COMMERCE_ITEMS.map((i) => ({ id: i.id.replace('integracion/', ''), label: i.label }));
  }
  return getCanalesDeSolucion(solucion).map((c) => ({ id: c.slug, label: c.label }));
};

const emptyForm = (solucionInicial: string): CasoPruebaFormData => ({
  nombre: '', solucionRelacionada: solucionInicial, campos: [], detalles: ''
});

export default function CasoPruebaFormModal({ projectId, solucionesActivas, modoArea = false, caso, onClose, onSaved }: CasoPruebaFormModalProps) {
  const { user, profile } = useAuth();
  const [form, setForm] = useState<CasoPruebaFormData>(
    caso
      ? { nombre: caso.nombre, solucionRelacionada: caso.solucionRelacionada, canalId: caso.canalId, canalNombre: caso.canalNombre, campos: caso.campos, detalles: caso.detalles }
      : emptyForm(modoArea ? '' : solucionesActivas[0] || '')
  );
  const [adjuntosExistentes, setAdjuntosExistentes] = useState<UploadedAttachment[]>(caso?.adjuntos || []);
  const [nuevosArchivos, setNuevosArchivos] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opcionesCanal = canalesDe(form.solucionRelacionada);

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setNuevosArchivos((prev) => [...prev, ...selected]);
    e.target.value = '';
  };
  const removeAdjuntoExistente = (url: string) => setAdjuntosExistentes((prev) => prev.filter((a) => a.url !== url));
  const removeNuevoArchivo = (index: number) => setNuevosArchivos((prev) => prev.filter((_, i) => i !== index));

  const handleSolucionChange = (solucion: string) => {
    setForm({ ...form, solucionRelacionada: solucion, canalId: undefined, canalNombre: undefined });
  };

  const handleCanalChange = (canalId: string) => {
    const canal = opcionesCanal.find((c) => c.id === canalId);
    setForm({ ...form, canalId: canalId || undefined, canalNombre: canal?.label });
  };

  const handleCampoChange = (index: number, campo: Partial<CampoPrueba>) => {
    const campos = [...form.campos];
    campos[index] = { ...campos[index], ...campo };
    setForm({ ...form, campos });
  };

  const addCampo = () => setForm({ ...form, campos: [...form.campos, { nombre: '', valor: '' }] });
  const removeCampo = (index: number) => setForm({ ...form, campos: form.campos.filter((_, i) => i !== index) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      if (caso) {
        await updateCasoPruebaDatos(
          projectId, caso.id,
          { nombre: form.nombre, campos: form.campos, detalles: form.detalles, adjuntosExistentes },
          nuevosArchivos
        );
      } else if (user) {
        await createCasoPrueba(projectId, form, nuevosArchivos, user.uid, profile?.name || user.email || '');
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el caso de prueba.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{caso ? 'Editar caso de prueba' : 'Nuevo caso de prueba'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre del escenario*</label>
            <input
              type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="ej. Creación de orden de compra" className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {solucionesActivas.length > 0 && (
            <div>
              <label className={labelClass}>{modoArea ? 'Área' : 'Solución*'}</label>
              <select
                value={form.solucionRelacionada}
                onChange={(e) => handleSolucionChange(e.target.value)}
                disabled={!!caso}
                className={`${inputClass} disabled:opacity-60`}
              >
                {modoArea && <option value="">Sin área</option>}
                {solucionesActivas.map((s) => <option key={s} value={s}>{SOLUCIONES[s]?.label || s}</option>)}
              </select>
            </div>
            )}
            {opcionesCanal.length > 0 && (
              <div>
                <label className={labelClass}>Canal</label>
                <select
                  value={form.canalId || ''}
                  onChange={(e) => handleCanalChange(e.target.value)}
                  disabled={!!caso}
                  className={`${inputClass} disabled:opacity-60`}
                >
                  <option value="">Sin canal específico</option>
                  {opcionesCanal.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Datos de prueba</label>
              <button type="button" onClick={addCampo} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                <Plus className="w-3.5 h-3.5" /> Agregar dato
              </button>
            </div>
            {form.campos.length === 0 ? (
              <p className="text-xs text-gray-500">Sin datos agregados todavía — ej. Sociedad, Proveedor, Material.</p>
            ) : (
              <div className="space-y-2">
                {form.campos.map((campo, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text" value={campo.nombre} onChange={(e) => handleCampoChange(i, { nombre: e.target.value })}
                      placeholder="Nombre (ej. Sociedad)" className={`${inputClass} flex-1`}
                    />
                    <input
                      type="text" value={campo.valor} onChange={(e) => handleCampoChange(i, { valor: e.target.value })}
                      placeholder="Valor (ej. 1234)" className={`${inputClass} flex-1`}
                    />
                    <button type="button" onClick={() => removeCampo(i)} className="text-gray-500 hover:text-red-400 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Adjuntos (capturas, logs)</label>
            <label className="flex items-center gap-2 justify-center border border-dashed border-white/20 rounded-lg p-3 text-xs text-gray-400 hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all">
              <Paperclip className="w-4 h-4" />
              Seleccionar archivos
              <input type="file" multiple onChange={handleAddFiles} className="hidden" />
            </label>

            {(adjuntosExistentes.length > 0 || nuevosArchivos.length > 0) && (
              <div className="mt-2 space-y-1.5">
                {adjuntosExistentes.map((a) => (
                  <div key={a.url} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{a.name}</span>
                    <button type="button" onClick={() => removeAdjuntoExistente(a.url)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {nuevosArchivos.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => removeNuevoArchivo(idx)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Detalles</label>
            <textarea
              value={form.detalles}
              onChange={(e) => setForm({ ...form, detalles: e.target.value })}
              rows={4}
              placeholder="Pasos del escenario, validaciones a realizar, resultado esperado..."
              className={inputClass}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando || !form.nombre.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
